import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, forkJoin, of } from 'rxjs';
import { AuthService } from '../../../_core/_services/auth.service';
import { UserRole } from '../../../_core/_model/user-role';
import { WithdrawalAdminListItemDto } from '../../../_core/_model/withdrawal';
import { MlOperationalAlertDto, WithdrawalRiskOperationalSummaryDto } from '../../../_core/_model/machine-learning';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-ml-withdrawal-risk',
  standalone: false,
  templateUrl: './admin-ml-withdrawal-risk.html',
  styleUrl: './admin-ml-withdrawal-risk.css'
})
export class AdminMlWithdrawalRisk implements OnInit {
  loading = false;
  error = '';
  summary: WithdrawalRiskOperationalSummaryDto | null = null;
  alerts: MlOperationalAlertDto[] = [];
  queue: WithdrawalAdminListItemDto[] = [];
  selected: WithdrawalAdminListItemDto | null = null;
  action = '';
  notes = '';
  gatewayReference = '';
  providerTransactionId = '';
  actionLoading = false;
  queueRestricted = false;
  currentRole: UserRole | null = null;

  constructor(
    private mlService: MachineLearningAdminService,
    private notify: NotificationService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => {
      this.currentRole = (user?.role as UserRole | undefined) ?? null;
      this.load();
    });
  }

  get canManageWithdrawals(): boolean {
    return this.currentRole === 'Admin' ||
      this.currentRole === 'SuperAdmin' ||
      this.currentRole === 'Finance';
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.queueRestricted = false;
    forkJoin({
      summary: this.mlService.getWithdrawalRiskSummary(),
      alerts: this.mlService.getWithdrawalRiskAlerts(),
      pending: this.canManageWithdrawals
        ? this.mlService.getWithdrawalReviewsByStatus('Pending').pipe(catchError(() => { this.queueRestricted = true; return of({ data: [] } as any); }))
        : of({ data: [] } as any),
      submitting: this.canManageWithdrawals
        ? this.mlService.getWithdrawalReviewsByStatus('Submitting').pipe(catchError(() => { this.queueRestricted = true; return of({ data: [] } as any); }))
        : of({ data: [] } as any),
      reconciliation: this.canManageWithdrawals
        ? this.mlService.getWithdrawalReviewsByStatus('ReconciliationRequired').pipe(catchError(() => { this.queueRestricted = true; return of({ data: [] } as any); }))
        : of({ data: [] } as any)
    }).subscribe({
      next: result => {
        this.summary = result.summary.data;
        this.alerts = result.alerts.data || [];
        this.queue = [
          ...(result.pending.data || []),
          ...(result.submitting.data || []),
          ...(result.reconciliation.data || [])
        ].sort((a, b) => new Date(a.riskReviewRequiredAt || a.createdAt).getTime() - new Date(b.riskReviewRequiredAt || b.createdAt).getTime());
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error.error?.error?.details || error.error?.message || error.message || 'Unable to load withdrawal-risk operations.';
        this.loading = false;
      }
    });
  }


  openAction(item: WithdrawalAdminListItemDto, action: string): void {
    if (!this.canManageWithdrawals) return;
    this.selected = item;
    this.action = action;
    this.notes = '';
    this.gatewayReference = item.gatewayReference || '';
    this.providerTransactionId = '';
  }

  closeAction(): void {
    this.selected = null;
    this.action = '';
  }

  submitAction(): void {
    if (!this.selected || !this.canManageWithdrawals) return;
    const id = this.selected.withdrawalId;
    this.actionLoading = true;
    const operation = this.action === 'approve'
      ? this.mlService.approveWithdrawalRiskReview(id, this.notes)
      : this.action === 'reject'
        ? this.mlService.rejectWithdrawalRiskReview(id, this.notes)
        : this.action === 'reopen'
          ? this.mlService.reopenWithdrawalRiskReview(id, this.notes)
          : this.mlService.markWithdrawalSubmittedAfterReconciliation(id, {
              notes: this.notes,
              gatewayReference: this.gatewayReference,
              providerTransactionId: this.providerTransactionId || null
            });

    operation.subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Withdrawal risk review updated.', 'Success');
        this.closeAction();
        this.load();
      },
      error: error => this.notify.showError(
        error?.error?.error?.details || error?.error?.message || 'Unable to update withdrawal risk review.',
        'Error'
      ),
      complete: () => this.actionLoading = false
    });
  }

  actionTitle(): string {
    switch (this.action) {
      case 'approve': return 'Approve withdrawal risk review';
      case 'reject': return 'Reject withdrawal risk review';
      case 'reopen': return 'Reopen after reconciliation';
      case 'submitted': return 'Mark payout as submitted';
      default: return 'Withdrawal review action';
    }
  }

  statusClass(status?: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'pending': return 'text-bg-warning';
      case 'submitting': return 'text-bg-primary';
      case 'reconciliationrequired': return 'text-bg-danger';
      case 'approved': return 'text-bg-success';
      case 'rejected': return 'text-bg-secondary';
      default: return 'text-bg-light text-dark border';
    }
  }

  riskClass(classification?: string | null): string {
    switch ((classification || '').toLowerCase()) {
      case 'critical': return 'text-bg-danger';
      case 'high': return 'text-bg-warning text-dark';
      case 'moderate': return 'text-bg-info';
      case 'low': return 'text-bg-success';
      default: return 'text-bg-secondary';
    }
  }

  alertClass(severity?: string | null): string {
    switch ((severity || '').toLowerCase()) {
      case 'critical': return 'alert-danger';
      case 'high': return 'alert-warning';
      default: return 'alert-info';
    }
  }
}
